import React, { useState, useEffect, useCallback } from 'react';
import {
  Save, Loader2, CheckCircle2, Circle,
  LayoutGrid, CalendarDays, ArrowRight
} from 'lucide-react';
import { auth } from '../config/firebase';
import { configuracaoService } from '../services/api';

const diasSemanaNomes = [
  { id: 0, nome: 'DOM' }, { id: 1, nome: 'SEG' }, { id: 2, nome: 'TER' },
  { id: 3, nome: 'QUA' }, { id: 4, nome: 'QUI' }, { id: 5, nome: 'SEX' },
  { id: 6, nome: 'SÁB' },
];

const FluxoAgendaTab = () => {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [config, setConfig] = useState({
    duracao_consulta: 30,
    intervalo_buffer: 10,
    timeout_sessao: 120,
    agenda_dias: diasSemanaNomes.map(d => ({
      dia_semana: d.id,
      ativo: d.id !== 0 && d.id !== 6,
      inicio: '08:00',
      fim: '18:00',
      intervalo_inicio: '12:00',
      intervalo_fim: '13:00'
    }))
  });

  const fetchData = useCallback(async () => {
    setInitialLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) return;

      const resAgenda = await configuracaoService.obterAgenda(user.uid).catch(() => ({ data: null }));

      if (resAgenda.data) {
        const d = resAgenda.data;
        const agendaFormatada = d.agenda_dias && d.agenda_dias.length > 0
          ? d.agenda_dias.map(dia => ({
              ...dia,
              intervalo_inicio: dia.intervalo_inicio || '12:00',
              intervalo_fim: dia.intervalo_fim || '13:00'
            }))
          : diasSemanaNomes.map(d => ({
              dia_semana: d.id,
              ativo: d.id !== 0 && d.id !== 6,
              inicio: '08:00',
              fim: '18:00',
              intervalo_inicio: '12:00',
              intervalo_fim: '13:00'
            }));

        setConfig(prev => ({
          ...prev,
          duracao_consulta: d.duracao_padrao_minutos || 30,
          intervalo_buffer: d.intervalo_buffer || 10,
          timeout_sessao: d.timeout_sessao || 120,
          agenda_dias: agendaFormatada,
        }));
      }
    } catch (error) {
      console.error("Erro ao carregar dados da agenda:", error);
    } finally {
      setInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDayToggle = (id) => {
    setConfig(p => ({ ...p, agenda_dias: p.agenda_dias.map(d => d.dia_semana === id ? { ...d, ativo: !d.ativo } : d) }));
  };

  const handleDayTimeChange = (id, field, val) => {
    setConfig(p => ({ ...p, agenda_dias: p.agenda_dias.map(d => d.dia_semana === id ? { ...d, [field]: val } : d) }));
  };

  const handleSave = async () => {
    setLoading(true);
    setSaveSuccess(false);
    try {
      const user = auth.currentUser;
      const dadosAgenda = {
        duracao_consulta: config.duracao_consulta,
        intervalo_buffer: config.intervalo_buffer,
        timeout_sessao: config.timeout_sessao,
        agenda_dias: config.agenda_dias
      };
      await configuracaoService.salvarAgenda(user.uid, dadosAgenda);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      alert("Erro ao salvar.");
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) return (
    <div className="flex flex-col items-center justify-center py-24 text-center text-slate-400 font-black uppercase text-xs tracking-[0.2em]">
      <Loader2 className="animate-spin text-[#1883FF] mb-4" size={42} />
      <p>Carregando Fluxo da Agenda...</p>
    </div>
  );

  return (
    <div className="bg-white rounded-[32px] p-6 sm:p-10 border border-slate-100 shadow-[0_20px_40px_-12px_rgba(0,0,0,0.03)] w-full box-border text-left">
      <div className="flex items-center gap-3 mb-6 border-b border-purple-50 pb-4">
        <CalendarDays size={18} className="text-[#1883FF]" />
        <h3 className="text-sm font-black uppercase italic text-slate-900 m-0">Regras Básicas</h3>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
        <div className="flex flex-col gap-2 text-left">
          <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Duração Padrão (min)</label>
          <input type="number" className="w-full bg-[#F8F7FF] border-2 border-transparent p-3.5 px-4 rounded-2xl text-sm font-bold text-slate-900 outline-none transition-all focus:bg-white focus:border-[#6D28D9]/30 focus:ring-4 focus:ring-[#6D28D9]/10" value={config.duracao_consulta} onChange={(e) => setConfig({...config, duracao_consulta: e.target.value})} />
        </div>
        <div className="flex flex-col gap-2 text-left">
          <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Intervalo (min)</label>
          <input type="number" className="w-full bg-[#F8F7FF] border-2 border-transparent p-3.5 px-4 rounded-2xl text-sm font-bold text-slate-900 outline-none transition-all focus:bg-white focus:border-[#6D28D9]/30 focus:ring-4 focus:ring-[#6D28D9]/10" value={config.intervalo_buffer} onChange={(e) => setConfig({...config, intervalo_buffer: e.target.value})} />
        </div>
        <div className="flex flex-col gap-2 text-left">
          <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Reset Bot (min)</label>
          <input type="number" className="w-full bg-[#F8F7FF] border-2 border-transparent p-3.5 px-4 rounded-2xl text-sm font-bold text-slate-900 outline-none transition-all focus:bg-white focus:border-[#6D28D9]/30 focus:ring-4 focus:ring-[#6D28D9]/10" value={config.timeout_sessao} onChange={(e) => setConfig({...config, timeout_sessao: e.target.value})} />
        </div>
      </div>

      <div className="flex items-center gap-3 mb-6 border-b border-purple-50 pb-4">
        <LayoutGrid size={18} className="text-[#1883FF]" />
        <h3 className="text-sm font-black uppercase italic text-slate-900 m-0">Horários de Atendimento</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-10">
        {config.agenda_dias.map((dia) => (
          <div key={dia.dia_semana} className={`p-4 rounded-2xl flex flex-col gap-3 transition-all ${dia.ativo ? 'bg-white border border-purple-50 shadow-[0_4px_12px_rgba(109,40,217,0.04)]' : 'bg-[#F8F7FF]'}`}>
            <div className="flex items-center gap-2.5 cursor-pointer text-xs font-black text-slate-900" onClick={() => handleDayToggle(dia.dia_semana)}>
              {dia.ativo ? <CheckCircle2 size={18} className="text-[#1883FF]" /> : <Circle size={18} className="text-slate-200" />}
              <span>{diasSemanaNomes[dia.dia_semana].nome}</span>
            </div>
            {dia.ativo && (
              <div className="flex flex-col gap-2 w-full">
                <div className="flex flex-wrap items-center gap-2 bg-[#F8F7FF] border border-purple-50/50 p-2 rounded-xl w-full">
                  <span className="text-[9px] font-black text-slate-400 uppercase w-full sm:w-auto mb-1 sm:mb-0 text-left pl-1">Turno</span>
                  <input type="time" className="flex-1 min-w-[80px] max-w-[110px] bg-white border border-purple-50 rounded-lg p-1.5 text-xs font-black text-slate-900 text-center outline-none focus:border-[#6D28D9]" value={dia.inicio} onChange={(e) => handleDayTimeChange(dia.dia_semana, 'inicio', e.target.value)} />
                  <ArrowRight size={14} className="text-[#6D28D9]/50" />
                  <input type="time" className="flex-1 min-w-[80px] max-w-[110px] bg-white border border-purple-50 rounded-lg p-1.5 text-xs font-black text-slate-900 text-center outline-none focus:border-[#6D28D9]" value={dia.fim} onChange={(e) => handleDayTimeChange(dia.dia_semana, 'fim', e.target.value)} />
                </div>
                <div className="flex flex-wrap items-center gap-2 bg-[#F8F7FF] border border-purple-50/50 p-2 rounded-xl w-full">
                  <span className="text-[9px] font-black text-orange-500 uppercase w-full sm:w-auto mb-1 sm:mb-0 text-left pl-1">Pausa</span>
                  <input type="time" className="flex-1 min-w-[80px] max-w-[110px] bg-white border border-purple-50 rounded-lg p-1.5 text-xs font-black text-slate-900 text-center outline-none focus:border-[#6D28D9]" value={dia.intervalo_inicio} onChange={(e) => handleDayTimeChange(dia.dia_semana, 'intervalo_inicio', e.target.value)} />
                  <ArrowRight size={14} className="text-[#6D28D9]/50" />
                  <input type="time" className="flex-1 min-w-[80px] max-w-[110px] bg-white border border-purple-50 rounded-lg p-1.5 text-xs font-black text-slate-900 text-center outline-none focus:border-[#6D28D9]" value={dia.intervalo_fim} onChange={(e) => handleDayTimeChange(dia.dia_semana, 'intervalo_fim', e.target.value)} />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <button
        className={`w-full h-14 border-none rounded-2xl text-white font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2.5 transition-all duration-400 cursor-pointer shadow-md active:scale-98 ${saveSuccess ? 'bg-[#059669] shadow-emerald-600/20' : 'bg-gradient-to-r from-[#1883FF] to-[#6D28D9] hover:opacity-90 shadow-lg shadow-[#6D28D9]/20 hover:-translate-y-0.5'}`}
        onClick={handleSave}
        disabled={loading}
      >
        {loading ? <Loader2 className="animate-spin" size={18} /> : saveSuccess ? <CheckCircle2 size={18} /> : <Save size={18} />}
        {loading ? "Processando..." : saveSuccess ? "Configurações Aplicadas!" : "Salvar Alterações"}
      </button>
    </div>
  );
};

export default FluxoAgendaTab;
